import { useState, useEffect, useRef } from 'react';
import { Navigation, Share2, Star, Camera } from 'lucide-react';
import type { View } from '../types';
import { cn } from '../lib/utils';
import { getImageUrl } from '../lib/imageUtils';

// How many confirmed views the widget surfaces, newest first.
const MAX_ITEMS = 3;

interface LatestConfirmedWidgetProps {
  views: View[];
  onViewClick: (view: View) => void;
  onShare: (view: View) => void;
  className?: string;
  'data-testid'?: string;
  /** Hide the "Last N" count when something else owns the header's right corner. */
  showCount?: boolean;
}

interface ViewTileProps {
  view: View;
  isNewest: boolean;
  onViewClick: (view: View) => void;
  onShare: (view: View) => void;
}

/**
 * One confirmed view as a photograph with its caption laid over it.
 *
 * Source and destination get a line each rather than sharing one. Around 40%
 * of confirmed views have a combined "source -> destination" longer than fits
 * on one line at this width — the longest in the data is 72 characters — so a
 * single line would routinely truncate the destination away entirely. Two
 * lines truncate independently and always show the start of both ends.
 */
function ViewTile({ view, isNewest, onViewClick, onShare }: ViewTileProps) {
  const [imageLoaded, setImageLoaded] = useState(false);

  // The chip only has room for one figure, not the full "x km / y mi" string.
  const shortDistance = `${view.distanceKm.toLocaleString()} km`;

  return (
    <div
      data-testid="latest-confirmed-tile"
      role="button"
      tabIndex={0}
      onClick={() => onViewClick(view)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onViewClick(view);
        }
      }}
      aria-label={`View from ${view.source} to ${view.destination}`}
      className={cn(
        'group/tile relative w-full overflow-hidden rounded-md cursor-pointer',
        'aspect-video bg-gray-900/50 border border-green-500/25',
        'transition-all duration-300 hover:border-green-400/60',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400/70'
      )}
    >
      <img
        src={getImageUrl(view, 'thumbnail')}
        alt=""
        onLoad={() => setImageLoaded(true)}
        className={cn(
          'h-full w-full object-cover transition-all duration-500',
          !imageLoaded && 'opacity-0',
          'group-hover/tile:scale-105'
        )}
        loading="lazy"
      />

      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/30 animate-pulse">
          <Camera className="h-5 w-5 text-muted-foreground/50" />
        </div>
      )}

      {/* Scrim. The caption sits on the photograph, so this carries the
          contrast budget — a bright sky behind white text is the failure case. */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-transparent" />

      {isNewest && (
        <div className="absolute top-1.5 left-1.5 rounded-full bg-green-600/90 px-1.5 py-0.5 text-[10px] font-semibold leading-[14px] tracking-wide text-white">
          NEW
        </div>
      )}

      <div className="absolute top-1.5 right-1.5 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium leading-4 text-white backdrop-blur-sm">
        {shortDistance}
      </div>

      <div className="absolute bottom-1.5 left-2 right-8">
        <div className="truncate text-xs font-semibold leading-4 text-white">
          {view.source}
        </div>
        <div className="flex items-center gap-1 text-[11px] leading-[15px] text-white/80">
          <Navigation className="h-2.5 w-2.5 flex-shrink-0 text-blue-300" />
          <span className="truncate">{view.destination}</span>
        </div>
      </div>

      {/* Share keeps its affordance without taking a row: visible on hover, and
          always reachable by keyboard. */}
      <button
        data-testid="share-button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onShare(view);
        }}
        aria-label={`Share view from ${view.source} to ${view.destination}`}
        className={cn(
          'absolute bottom-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-full',
          'bg-black/60 text-white backdrop-blur-sm transition-all duration-200',
          'opacity-0 pointer-events-none',
          'group-hover/tile:opacity-100 group-hover/tile:pointer-events-auto',
          'focus-visible:opacity-100 focus-visible:pointer-events-auto',
          'hover:bg-black/80 hover:text-green-300',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400/70'
        )}
      >
        <Share2 className="h-3 w-3" />
      </button>
    </div>
  );
}

export function LatestConfirmedWidget({
  views,
  onViewClick,
  onShare,
  className,
  'data-testid': testId,
  showCount = true
}: LatestConfirmedWidgetProps) {
  const [latestViews, setLatestViews] = useState<View[]>([]);
  const previousViewIdsRef = useRef<string>('');

  useEffect(() => {
    if (views && views.length > 0) {
      // Filter for confirmed views only
      const confirmedViews = views.filter(view => view.status === 'confirmed');

      if (confirmedViews.length > 0) {
        // Sort by ID to get the latest (assuming higher ID = more recent)
        const sortedViews = confirmedViews.sort((a, b) =>
          parseInt(b.id, 10) - parseInt(a.id, 10)
        );
        const newLatestViews = sortedViews.slice(0, MAX_ITEMS);

        // Only re-render the list when its membership actually changes
        const newIds = newLatestViews.map(view => view.id).join(',');
        if (previousViewIdsRef.current !== newIds) {
          setLatestViews(newLatestViews);
          previousViewIdsRef.current = newIds;
        }
      }
    }
  }, [views]);

  if (latestViews.length === 0) {
    return null;
  }

  return (
    <div
      data-testid={testId}
      className={cn(
        'relative rounded-lg overflow-hidden transition-all duration-300',
        'bg-gradient-to-br from-green-900/20 to-emerald-900/10 border border-green-500/30 shadow-lg',
        'hover:shadow-xl hover:shadow-green-500/10 hover:border-green-400/50',
        'backdrop-blur-sm',
        className
      )}
    >
      {/* Sci-fi corner accents - green theme */}
      <div className="absolute top-0 left-0 w-[40px] h-[1px] bg-gradient-to-r from-green-400/70 to-transparent z-10" />
      <div className="absolute top-0 left-0 w-[1px] h-[40px] bg-gradient-to-b from-green-400/70 to-transparent z-10" />
      <div className="absolute top-0 right-0 w-[40px] h-[1px] bg-gradient-to-l from-green-400/70 to-transparent z-10" />
      <div className="absolute top-0 right-0 w-[1px] h-[40px] bg-gradient-to-b from-green-400/70 to-transparent z-10" />

      {/* Header */}
      <div className="px-3.5 pt-3.5 pb-2.5 border-b border-green-500/20">
        <div className="flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-[7px] text-[15px] font-bold leading-[22px] text-green-400">
            <Star className="h-[17px] w-[17px] flex-shrink-0 text-green-400" />
            Latest Confirmed
          </h3>
          {showCount && (
            <span className="flex-shrink-0 text-[11px] leading-4 text-green-400/75">
              Last {latestViews.length}
            </span>
          )}
        </div>
      </div>

      {/* Tiles, newest first */}
      <div className="flex flex-col gap-1.5 p-2.5">
        {latestViews.map((view, index) => (
          <ViewTile
            key={view.id}
            view={view}
            isNewest={index === 0}
            onViewClick={onViewClick}
            onShare={onShare}
          />
        ))}
      </div>
    </div>
  );
}
